import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ShBooleanDialogComponent, ShDynamicDialogService } from '@stylehaven/components';
import { Observable, Observer } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { SavedQuery } from '../../entities/saved-queries/saved-query.entity';
import { LoadingBehaviorSubject } from '../../helpers';
import { OperatorFactory } from '../../operator-factory/services/operator-factory.service';
import { RecordActionItem } from '../../record/entities/action/record-action-item.class';
import { AbstractActionCreator } from '../../services/actions/abstract-action.creator';
import { SavedQueriesService } from '../../services/saved-queries/saved-queries.service';

@Injectable()
export class DeleteSavedQueryActionCreator extends AbstractActionCreator {
    constructor(
        private readonly translateService: TranslateService,
        private dialogService: ShDynamicDialogService,
        private savedQueriesService: SavedQueriesService,
        private operatorFactory: OperatorFactory
    ) {
        super();
    }

    getDeleteSavedQueryAction(loader$: LoadingBehaviorSubject): RecordActionItem<SavedQuery> {
        const label = this.translateService.instant('actions.common.delete');
        const action = new RecordActionItem<SavedQuery>(label);
        action.enablePredicate = query => query.Context.IsDeletable;
        action.disabledInfoMsg = this.translateService.instant('components.saved-queries.disabled.delete');
        action.callback = (query, observer) => this.openConfirmDialog(loader$, query, observer);
        return action;
    }

    private openConfirmDialog(loader$: LoadingBehaviorSubject, query: SavedQuery, observer: Observer<any>): void {
        this.dialogService
            .open<boolean>(ShBooleanDialogComponent, {
                title: this.translateService.instant('actions.common.delete'),
                data: {
                    yes: this.translateService.instant('actions.common.delete'),
                    no: this.translateService.instant('actions.common.cancel'),
                    message: this.translateService.instant('components.saved-queries.delete-confirm-dialog.message'),
                    warningMessage:
                        query.Groups.length > 0
                            ? this.translateService.instant('components.saved-queries.delete-confirm-dialog.shared-warning')
                            : undefined
                }
            })
            .afterClosed$.pipe(
                take(1),
                filter(accepted => accepted),
                switchMap(() => this.handleDelete(loader$, query))
            )
            .subscribe(observer);
    }

    private handleDelete(loader$: LoadingBehaviorSubject, query: SavedQuery): Observable<void> {
        loader$.startLoading();
        const delete$ = this.savedQueriesService.delete(query.Id);

        return delete$.pipe(
            this.operatorFactory.withToastUpdates({
                startedMsgKey: 'toasts.saved-queries.delete.START',
                successMsgKey: 'toasts.saved-queries.delete.SUCCESS',
                errorMsgKey: 'toasts.saved-queries.delete.FAIL'
            }),
            this.operatorFactory.withLoadingHandler(loader$)
        );
    }
}
